import { useEffect, useState } from 'react';

import { useMyShelf } from '@/features/library/hooks/useMyShelf.js';

import {
  useCollectionDetails,
  useCreateCollection,
  useDeleteCollection,
  useUpdateCollection,
} from '../hooks/useMyCollections.js';

const EMOJI_CHOICES = [
  '☀️','🔥','🌙','✨','🌸','🐉','💌','📖','🌹','🍃','🌿','🪴','🕯️','🐈',
  '☕','🌷','🍄','🏆','📚','🔮','💎','🦋','🍂','⭐','🌊','🎀','🍷','🎭',
];

interface CollectionFormModalProps {
  /** ID d'une collection existante à éditer, ou null pour créer. */
  readonly existingId: string | null;
  readonly onClose: () => void;
}

export function CollectionFormModal({
  existingId,
  onClose,
}: CollectionFormModalProps): JSX.Element {
  const { data: shelfData } = useMyShelf();
  const { data: detailsData } = useCollectionDetails(existingId);
  const createMutation = useCreateCollection();
  const updateMutation = useUpdateCollection();
  const deleteMutation = useDeleteCollection();

  const existing = detailsData?.collection ?? null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState('📚');
  const [bookIds, setBookIds] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setDescription(existing.description ?? '');
      setEmoji(existing.emoji);
      setBookIds(existing.bookIds);
    }
  }, [existing]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const allBooks = shelfData?.items.map((e) => e.book) ?? [];
  const submitting =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const handleSave = (): void => {
    if (name.trim().length === 0) return;
    const payload = {
      name: name.trim(),
      description: description.trim().length > 0 ? description.trim() : undefined,
      emoji,
      bookIds,
    };
    if (existingId) {
      updateMutation.mutate(
        { id: existingId, patch: payload },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(payload, { onSuccess: () => onClose() });
    }
  };

  const handleDelete = (): void => {
    if (!existingId) return;
    deleteMutation.mutate(existingId, { onSuccess: () => onClose() });
  };

  const toggleBook = (id: string): void => {
    setBookIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id],
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="collection-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="modal-overlay-anim fixed inset-0 z-50 flex items-end justify-center bg-brown/55 p-0 backdrop-blur md:items-center md:p-6"
    >
      <div className="modal-content-anim flex max-h-[90vh] w-full max-w-2xl flex-col overflow-y-auto rounded-t-3xl bg-cream-light shadow-xl md:max-h-[85vh] md:rounded-3xl">
        <header className="flex items-center justify-between px-6 pt-5 md:px-8">
          <h2
            id="collection-modal-title"
            className="font-serif text-xl font-semibold text-brown md:text-2xl"
          >
            {existingId ? '✏️ Modifier la collection' : '✨ Nouvelle collection'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-paper text-brown shadow-sm hover:bg-cream-dark"
          >
            ✕
          </button>
        </header>

        <div className="flex flex-col gap-5 px-6 py-5 md:px-8 md:py-6">
          <div>
            <label className="font-serif text-sm font-semibold text-brown" htmlFor="col-name">
              Nom
            </label>
            <input
              id="col-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ex. Lectures de cheminée"
              className="mt-1.5 w-full rounded-full bg-paper px-4 py-2.5 text-sm text-brown ring-1 ring-cream-dark focus:outline-none focus:ring-terracotta"
            />
          </div>

          <div>
            <label className="font-serif text-sm font-semibold text-brown" htmlFor="col-desc">
              Description
            </label>
            <input
              id="col-desc"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="optionnel"
              className="mt-1.5 w-full rounded-full bg-paper px-4 py-2.5 text-sm text-brown ring-1 ring-cream-dark focus:outline-none focus:ring-terracotta"
            />
          </div>

          <div>
            <p className="font-serif text-sm font-semibold text-brown">Emoji</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {EMOJI_CHOICES.map((e) => {
                const active = e === emoji;
                return (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setEmoji(e)}
                    aria-pressed={active}
                    className={`flex h-9 w-9 items-center justify-center rounded-full text-lg transition-colors ${
                      active
                        ? 'bg-terracotta ring-2 ring-terracotta-dark'
                        : 'bg-paper ring-1 ring-cream-dark hover:bg-cream-dark'
                    }`}
                  >
                    {e}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="font-serif text-sm font-semibold text-brown">
              Livres
              <span className="ml-2 font-sans font-normal text-brown-light">
                ({bookIds.length} sélectionné{bookIds.length > 1 ? 's' : ''})
              </span>
            </p>
            {allBooks.length === 0 ? (
              <p className="mt-2 text-sm text-brown-soft">
                Tu n'as pas encore de livre en bibliothèque.
              </p>
            ) : (
              <div className="mt-1.5 grid max-h-64 grid-cols-1 gap-1 overflow-y-auto rounded-2xl bg-paper p-2 ring-1 ring-cream-dark sm:grid-cols-2">
                {allBooks.map((b) => {
                  const selected = bookIds.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBook(b.id)}
                      className={`flex items-center gap-2 rounded-xl px-2 py-1.5 text-left text-xs transition-colors ${
                        selected
                          ? 'bg-terracotta/15 ring-1 ring-terracotta'
                          : 'hover:bg-cream-dark'
                      }`}
                    >
                      <span
                        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                          selected
                            ? 'bg-terracotta text-cream-light'
                            : 'bg-cream-dark text-brown-soft'
                        }`}
                        aria-hidden
                      >
                        {selected ? '✓' : ''}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        <span className="font-semibold text-brown">{b.title}</span>
                        <span className="text-brown-soft"> · {b.author}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-cream-dark px-6 py-4 md:px-8">
          {existingId ? (
            confirming ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setConfirming(false)}
                  className="rounded-full bg-paper px-3 py-1.5 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={submitting}
                  className="rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold text-cream-light hover:bg-red-600 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Suppression…' : 'Confirmer'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-xs font-semibold text-brown-soft hover:text-red-500"
              >
                🗑️ Supprimer
              </button>
            )
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-paper px-4 py-2 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={submitting || name.trim().length === 0}
              className="rounded-full bg-terracotta px-4 py-2 text-xs font-semibold text-cream-light hover:bg-terracotta-dark disabled:opacity-50"
            >
              {submitting ? 'Enregistrement…' : existingId ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
